/**
 * A gate node selected on the canvas (Feature 79 D22, revision 1 D27): what
 * belongs to the whole gate. Its name; If-else or Switch, and a switch's
 * field; every condition in the order tried, each with what it tests and its
 * steps, moved up or down by buttons and opening its own condition node,
 * where it is edited; Add else if or Add a case; the else or default on or
 * off; Remove gate.
 *
 * A gate left with one branch and no else shrinks back into the gate link it
 * grew from (D12), and that link is opened in its place.
 */
import type { LinkageDecision } from '@optum/api-client/types';

import { Button } from '../../ui/Button.js';
import { Field } from '../../ui/Field.js';
import { Banner, IconButton, Stack, Typography } from '../../ui/finixt.js';
import { SegmentedControl } from '../../ui/SegmentedControl.js';
import { Select } from '../../ui/Select.js';
import { Toggle } from '../../ui/Toggle.js';
import { kindLabel, stepsOf, testLines } from './condition-words.js';
import { ELSE, conditionId, conditionsOf } from './conditions.js';
import {
  addBranch,
  decisionOf,
  moveBranch,
  removeDecision,
  renameDecision,
  setElse,
  setMode,
  setSwitchRef,
} from './decisions.js';
import type { Draft } from './draft.js';
import { InspectorPanel } from './InspectorPanel.js';
import { NameField } from './NameField.js';
import { storedOption } from './positions.js';
import { PositionHint } from './PositionHint.js';
import {
  forPlace,
  PICKER_WIDTH,
  pickerOptions,
  referencesFor,
  type Schemas,
} from './references.js';
import { renameRefusal } from './rename.js';
import type { Selection } from './selection.js';

const CARD = {
  border: '1px solid var(--border-hairline)',
  borderRadius: 'var(--radius-card)',
  padding: 9,
} as const;
// A row's main area is a plain button that opens its condition node.
const ROW_BUTTON = {
  flex: 1,
  minWidth: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
  padding: 0,
  border: 'none',
  background: 'transparent',
  color: 'inherit',
  font: 'inherit',
  textAlign: 'left',
  cursor: 'pointer',
} as const;

export function GateInspector({
  draft,
  name,
  schemas,
  onChange,
  onSelect,
}: {
  draft: Draft;
  name: string;
  schemas: Schemas;
  onChange: (draft: Draft) => void;
  onSelect: (selection: Selection) => void;
}) {
  const decision = decisionOf(draft, name);
  if (!decision) return null;
  const switching = decision.mode === 'switch';
  const branches = conditionsOf(decision).filter((c) => c.number !== null);

  /** Apply an edit; when it shrank the gate back into a gate link, open that link. */
  const apply = (next: Draft) => {
    onChange(next);
    if (decisionOf(next, name)) return;
    const gate = next.links.find((l) => l.from === decision.from && l.branch === undefined);
    onSelect(gate ? { kind: 'edge', from: gate.from, to: gate.to } : null);
  };
  const references = referencesFor(draft, { kind: 'link', from: decision.from }, schemas);

  return (
    <InspectorPanel>
      <Typography variant="eyebrow">Gate node</Typography>
      <NameField
        id="gate-name"
        label="Name"
        value={name}
        refusal={(next) => renameRefusal(draft, name, next)}
        onCommit={(next) => {
          // A rename is no shrink: the gate is opened under its new name.
          onChange(renameDecision(draft, name, next));
          onSelect({ kind: 'node', id: next });
        }}
      />
      {!decision.from && (
        <Banner tone="warn" title="Follows nothing yet">
          Drag a link from first or a step into the diamond. Save waits until it has one.
        </Banner>
      )}
      <Typography variant="meta">
        {decision.from && (
          <>
            After <span className="mono">{decision.from}</span>.{' '}
          </>
        )}
        Tried in order: the first that holds runs, and only its steps.
      </Typography>
      <SegmentedControl
        items={[
          { value: 'if', label: 'If-else' },
          { value: 'switch', label: 'Switch' },
        ]}
        value={decision.mode}
        onChange={(mode) => apply(setMode(draft, name, mode as LinkageDecision['mode']))}
      />
      {switching && (
        <Field id="switch-ref" label="Switch on">
          <Select
            searchable
            id="switch-ref"
            value={decision.ref ?? ''}
            panelWidth={PICKER_WIDTH}
            searchPlaceholder="Search a field…"
            options={[
              { value: '', label: 'no field yet' },
              ...(decision.ref && !references.some((r) => r.value === decision.ref)
                ? [storedOption(decision.ref, 'not upstream')]
                : []),
              ...pickerOptions(forPlace(references, 'one')),
            ]}
            onChange={(ref) => apply(setSwitchRef(draft, name, ref))}
          />
        </Field>
      )}
      {switching && <PositionHint path={decision.ref} />}
      <Typography variant="body" style={{ fontWeight: 'var(--weight-semibold)' }}>
        Conditions, in the order tried
      </Typography>
      {branches.map((c, i) => (
        <div key={c.id} style={{ ...CARD, display: 'flex', alignItems: 'flex-start', gap: 7 }}>
          <span className="mono" style={{ color: 'var(--text-soft)' }}>
            {c.number}
          </span>
          <button
            type="button"
            aria-label={`Open ${c.branch}`}
            style={ROW_BUTTON}
            onClick={() => onSelect({ kind: 'node', id: c.id })}
          >
            <span>
              <span style={{ fontWeight: 'var(--weight-semibold)' }}>{kindLabel(c)}</span>{' '}
              <span className="mono">{c.branch}</span>
            </span>
            {testLines(decision, c.branch).map((line) => (
              <Typography key={line} variant="meta">
                {line}
              </Typography>
            ))}
            <Typography variant="meta">{stepsOf(draft, name, c.branch)}</Typography>
          </button>
          <IconButton
            icon="arrow_upward"
            size={26}
            tone="bare"
            aria-label={`Move ${c.branch} up`}
            disabled={i === 0}
            onClick={() => apply(moveBranch(draft, name, c.branch, -1))}
          />
          <IconButton
            icon="arrow_downward"
            size={26}
            tone="bare"
            aria-label={`Move ${c.branch} down`}
            disabled={i === branches.length - 1}
            onClick={() => apply(moveBranch(draft, name, c.branch, 1))}
          />
        </div>
      ))}
      <Button
        variant="ghost"
        size="sm"
        icon="add"
        onClick={() => {
          const next = addBranch(draft, name);
          onChange(next);
          // The new condition opens on its own node, where it is filled in.
          const added = decisionOf(next, name)?.branches.at(-1);
          if (added) onSelect({ kind: 'node', id: conditionId(name, added.name) });
        }}
      >
        {switching ? 'Add a case' : 'Add else if'}
      </Button>
      <div style={{ ...CARD, background: 'var(--surface-sunken)' }}>
        <Stack gap={5}>
          <Field
            id="gate-else"
            label={switching ? 'Default' : 'Else'}
            hint={
              decision.else
                ? `Runs when nothing above holds ${stepsOf(draft, name, ELSE)}`
                : 'Off: when nothing holds, nothing runs.'
            }
          >
            <Toggle
              checked={decision.else}
              aria-label={switching ? 'Default' : 'Else'}
              onChange={(on) => apply(setElse(draft, name, on))}
            />
          </Field>
        </Stack>
      </div>
      <Button
        variant="danger"
        size="sm"
        onClick={() => {
          onChange(removeDecision(draft, name));
          onSelect(null);
        }}
      >
        Remove gate
      </Button>
    </InspectorPanel>
  );
}
